import React, { useEffect, useState } from 'react';
import { Form, notification } from 'components/ds';
import PageContainer from 'modules/account/components/PageContainer/PageContainer';
import { useNavigate } from 'react-router-dom';
import ProgressStepper from 'components/ProgressStepper';
import { useGetTemplate } from 'modules/account/configs/SchemaConfig';
import { PasswordInput } from './OnboardingConfigPassword.styles';
import PasswordStrengthChecker from '../../../components/PasswordStrengthChecker/PasswordStrengthChecker';
import { useOtp } from '../../../hooks/useOtp';
import { RouteEnum } from '../../../constantes/RouteEnum';
import { useDynamicFieldsStorage } from '../../../hooks/useDynamicFieldsStorage';
import { useUpdateUserData } from '../../../hooks/useUpdateUserData';
import { useSchema } from '../../../hooks/useSchema';
import { CreatePasswordRequest } from '../../../_types/password.types';
import { ApiLocalStorage } from '../../../../../utils/ApiLocalStorage';
import { usePassword } from '../../../hooks/usePassword';
import { SCHEMA_STORAGE_KEY } from 'modules/account/services/SchemaService';
import { createUserData } from '../../../constantes/UserDataCreationService';
import TurnstileCaptcha from 'components/TurnstileCaptcha';
import { isPasswordFormValid } from '../../../constantes/passwordConstants';

interface SchemaItem {
  id: string;
  fileId: string;
}

interface PasswordConfig {
  password: string;
  confirmPassword: string;
}

export const OnboardingConfigPassword: React.FC = () => {
  const [form] = Form.useForm();
  const [passwordConfig, setPasswordConfig] = useState<PasswordConfig>({
    password: '',
    confirmPassword: ''
  });
  const navigate = useNavigate();
  const {updateUserData, isUpdating} = useUpdateUserData();
  const {otpId, otpCode} = useOtp();
  const {getSchemaId} = useSchema();
  const {createPassword, isLoading: createPasswordLoading} = usePassword()
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [captchaKey, setCaptchaKey] = useState(0);
  
  const {data: pageConfig = {}} = useGetTemplate('username');
  const {registrationData, clearStorageData} = useDynamicFieldsStorage();
  
  useEffect(() => {
    const getGeolocation = async () => {
      try {
        if (navigator.geolocation) {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              timeout: 10000,
              enableHighAccuracy: true
            });
          });
          ApiLocalStorage.set('lt', position.coords.latitude.toString());
          ApiLocalStorage.set('lg', position.coords.longitude.toString());
        }
      } catch (error) {
        ApiLocalStorage.set('lt', '');
        ApiLocalStorage.set('lg', '');
      }
    }
    
    getGeolocation();
  }, []);
  
  const handleVerify = (token: string) => {
    setCaptchaToken(token);
  }
  
  const handleUpdateUserData = async () => {
    try {
      const schemaId = getSchemaId();
      
      if (!schemaId) {
        notification.error({
          message: 'Erro de validação',
          description: 'Schema não encontrado. Por favor, tente novamente.'
        });
        
        return;
      }
      
      const username = ApiLocalStorage.get('@pwa-username');
      const schemaData = ApiLocalStorage.get(SCHEMA_STORAGE_KEY);
      
      const termsData = schemaData.terms.map((item: SchemaItem) => ({
        termId: item.id,
        fileId: item.fileId
      }));
      
      const userData = createUserData(schemaId, registrationData, username, termsData);
      
      const updatedUserData = {
        ...userData,
        userData: {
          ...userData.userData,
          onboardingFinished: true
        }
      };
      
      updateUserData(updatedUserData, {
        onSuccess: () => {
          clearStorageData();
          navigate(RouteEnum.FINISH);
        },
        onError: () => {
          notification.error({
            message: 'Erro ao atualizar dados',
            description: 'Não foi possível atualizar seus dados. Por favor, tente novamente.'
          });
        }
      });
    } catch (error) {
      notification.error({
        message: 'Erro inesperado',
        description: 'Ocorreu um erro ao processar sua solicitação.'
      });
    }
  };
  
  const handleFinish = async () => {
    try {
      const values = await form.validateFields();
      
      if (!otpId || !otpCode) {
        notification.error({
          message: 'Erro de validação',
          description: 'Dados de validação não encontrados. Por favor, tente novamente.'
        });
        
        return;
      }
      
      const passwordData: CreatePasswordRequest = {
        password: values.password,
        confirmPassword: values.confirmPassword,
        captchaToken
      };
      
      const success = await createPassword(passwordData);
      
      if (success) {
        handleUpdateUserData();
      }
    } catch (error) {
      // Reset captcha when operation fails
      setCaptchaToken('');
      setCaptchaKey(prev => prev + 1);
      
      // Error de validação do form
      console.error('Erro de validação:', error);
    }
  };
  
  const validatePassword = (value: string, type: string) => {
    setPasswordConfig({
      ...passwordConfig,
      [type]: value
    });
  };
  
  const passwordRules = [
    {required: true, message: 'Por favor, insira sua senha!'}
  ];
  
  const confirmPasswordRules = [
    {required: true, message: 'Por favor, confirme sua senha!'},
    ({getFieldValue}: any) => ({
      validator(_: any, value: string) {
        if (!value || getFieldValue('password') === value) {
          return Promise.resolve();
        }
        
        return Promise.reject(new Error('As senhas não correspondem!'));
      }
    })
  ];
  
  const isLoading = createPasswordLoading || isUpdating;
  const isButtonDisabled = !isPasswordFormValid(passwordConfig?.password, passwordConfig?.confirmPassword);
  
  return (
    <PageContainer
      title={pageConfig?.title || ''}
      navigateBack={pageConfig?.navigateBack?.()}
      buttonText='Criar senha'
      thereIsButton
      onClick={handleFinish}
      loading={isLoading}
      disabled={isLoading || !captchaToken || isButtonDisabled
      }
    >
      <ProgressStepper
        totalSteps={pageConfig.totalSteps}
        currentStep={pageConfig.currentStep}
      />
      
      <Form
        form={form}
        layout='vertical'
        onFinish={handleFinish}
      >
        <Form.Item name='password' rules={passwordRules}>
          <PasswordInput
            placeholder='Senha'
            size='large'
            onChange={(e) => validatePassword(e.target.value, 'password')}
          />
        </Form.Item>
        
        <PasswordStrengthChecker password={passwordConfig.password}/>
        
        <Form.Item
          name='confirmPassword'
          dependencies={['password']}
          rules={confirmPasswordRules}
        >
          <PasswordInput
            placeholder='Confirmar senha'
            size='large'
            onChange={(e) => validatePassword(e.target.value, 'confirmPassword')}
          />
        </Form.Item>
        <TurnstileCaptcha onVerify={handleVerify} key={captchaKey}/>
      </Form>
    </PageContainer>
  );
};
